import { defineStore } from 'pinia'
import { ref } from 'vue'

import * as ls from '@/api/ls'
import type {
  Customer,
  Deployment,
  License,
  LicenseReq,
  AgentConfigTemplate,
  MetricDefinition,
  ProductSpec,
  RebindRequest,
  Reseller,
} from '@/api/ls'
import * as reseller from '@/api/reseller'
import type { ResellerCustomer } from '@/api/reseller'
import { useAuthStore } from '@/stores/auth'

/** 後台各分頁共用的資料與載入器。mutation 由各 view 呼叫 ls.* 後再 reload 對應清單。 */
export const useDataStore = defineStore('data', () => {
  const customers = ref<Customer[]>([])
  const licenses = ref<License[]>([])
  /** keyset 續頁游標（null＝已載完）；`loadLicenses` 設、`loadMoreLicenses` 消費。 */
  const licensesNextCursor = ref<string | null>(null)
  /** 批121：四個狀態桶的全表筆數（`loadLicenseCounts` 設）；null＝還沒拿到。 */
  const licenseCounts = ref<ls.LicenseCounts | null>(null)
  /** 目前這份清單的伺服器端過濾條件——續頁一定要帶同一組，否則第二頁是別的東西。 */
  // ★ 批121：`bucket`（狀態籤）**一定要在這個型別裡**——`loadMoreLicenses` 原樣帶回去，
  //   漏了的話「載入更多」會載到全部授權的下一頁，而畫面上完全看不出來（那正是本批要修的症狀）。
  const licenseFilter = ref<{
    customerId?: string
    hostId?: string
    product?: string
    resellerId?: string
    bucket?: string
  }>({})
  const deployments = ref<Deployment[]>([])
  const deploymentsNextCursor = ref<string | null>(null)
  const deploymentFilter = ref<{ customerId?: string; hostId?: string; resellerId?: string; product?: string }>({})
  const rebinds = ref<RebindRequest[]>([])
  const rebindsNextCursor = ref<string | null>(null)
  const requests = ref<LicenseReq[]>([])
  const requestsNextCursor = ref<string | null>(null)
  // ★ 批121：`status`／`product`／`onlyReturned` 都在這裡，`loadMoreRequests` 原樣帶回去
  //   ——漏了的話「載入更多」會載到全部申請的下一頁，而畫面上完全看不出來。
  const requestFilter = ref<{
    origin?: string
    status?: string
    resellerId?: string
    platformId?: string
    product?: string
    /** 批155：核發方式（`platform`＝平台自動核准）。 */
    issuedVia?: string
    onlyReturned?: boolean
  }>({})
  /** 批121：五個狀態桶＋`returned` 的全表筆數（`loadRequestCounts` 設）；null＝還沒拿到。 */
  const requestCounts = ref<ls.LicenseRequestCounts | null>(null)
  const customersLoaded = ref(false)
  /** keyset 續頁游標（null＝已載完）。**客戶清單同時是全站對照表**，見 `loadCustomers`。 */
  const customersNextCursor = ref<string | null>(null)
  /**
   * 批93 多產品：全部產品的憑證設計＋套裝（`GET /api/admin/products` 一次回齊）。
   *
   * 這是**全 SPA 唯一**的憑證設計來源——核發框的模組／配額編輯器、清單頁把模組鍵翻成
   * 中文、套裝名顯示，全部靠它。照 `ensureCustomers` 的懶載慣例（進到需要的頁才打一次）。
   * `GET /products` 在批93 收尾放寬到 `require_any_admin_user`（純標籤與結構、零敏感
   * 資訊）——**operator 也讀得到**，部署頁的配額對話框才不會退化成一排原始英文 key。
   * 呼叫端仍要 catch：載入失敗時 `lib/quotas.ts` 的 spec=null 分支會接手。
   */
  const products = ref<ProductSpec[]>([])
  const productsLoaded = ref(false)
  /**
   * 批112：站台指標的**顯示定義**（`GET /api/admin/metric-defs` 一次回齊、不分頁）。
   *
   * 與 `products` 的分工要記清楚：那邊是產品**宣告**的憑證設計（模組／配額／行為參數），
   * 這邊是我方營運人工維護的**詮釋**（產品回報上來的英文鍵怎麼印）。兩者資料流向相反、
   * 版本綁定也相反——不要把它接回 `ProductSpec`。
   *
   * 讀端點是 `require_staff`（operator 也讀得到，否則部署頁的指標區段退化成英文鍵）。
   * ★ **沒有代理商分支**：代理商 realm 沒有對應端點，所以只從代理商到不了的畫面呼叫
   *   （部署頁、`/metric-defs`）。呼叫端一律 catch——載不到就退化成原始鍵。
   */
  const metricDefs = ref<MetricDefinition[]>([])
  const metricDefsLoaded = ref(false)
  /**
   * 批112／1.0.44：**設定樣板**（`GET /api/admin/agent-config-templates` 一次回齊、不分頁）。
   *
   * 「一台代管主機要提供給站台的環境變數」的欄位宣告。與 `products` 的分工：那邊是產品
   * 宣告的**憑證設計**（進授權 payload），這邊是我方維護的**主機層執行環境**（下發到
   * 站台 `.env`）。兩者資料流向不同、也不再互相參照。
   *
   * 讀端點是 `require_staff`（主機詳情頁要拿它畫「可掛載的樣板」下拉）。
   * ★ **沒有代理商分支**：代理商 realm 沒有對應端點，所以只從代理商到不了的畫面呼叫。
   *   呼叫端一律 catch——載不到就只是掛不了新樣板，既有掛載照樣顯示。
   */
  const agentConfigTemplates = ref<AgentConfigTemplate[]>([])
  const agentConfigTemplatesLoaded = ref(false)
  /**
   * 批93-C：代理商公司清單（客戶頁的「代理商」欄／篩選／指派下拉都靠它）。
   *
   * ⚠️ `GET /api/admin/resellers` 目前是 **superadmin** 端點——admin 打會 403。
   * 所以 `ensureResellers()` 吞掉錯誤、`resellersDenied` 標記起來，呼叫端在拿不到清單時
   * 退化成「用客戶列自帶的 `reseller_name` 組篩選選項」（見 `CustomersView`）。
   */
  const resellers = ref<Reseller[]>([])
  const resellersLoaded = ref(false)
  const resellersDenied = ref(false)

  /**
   * 全站共用的客戶快取（別的頁靠它把 `customer_id` 翻成名字、六個下拉靠它列選項）。
   *
   * ★★ 批93-D：後端改成 keyset 分頁之後，這一份快取是**唯一需要「盡量完整」**的清單
   *   ——所以這裡顯式帶 `limit`＝後端上限 200，而不是用預設的 50。用意不是繞過分頁：
   *   真正被消滅的是「一個 query 就把整張表拉進記憶體」的無界查詢，200 是硬上限。
   * ⚠️ 客戶數真的超過 200 時，`customersNextCursor` 會非 null，而別的頁的下拉會少列
   *   （`customerName()` 回「—」）。那一天要做的**不是**再把 limit 調大，而是把下拉換成
   *   伺服器端搜尋——API 契約（`limit`／`cursor`）已經就位。`CustomersView` 自己有
   *   「載入更多」，所以清單頁本身不受此限。
   */
  const CUSTOMER_LOOKUP_LIMIT = 200

  /**
   * 代理商 realm 的客戶列 → 我方後台的 `Customer` 形狀（批99-B）。
   *
   * ★ 為什麼要轉而不是放寬型別：六個代管 view（開站精靈、主機詳情、任務…）讀的是
   *   `store.customers`，它們對 `Customer` 的欄位（`kind`／`profile_completed_at`）
   *   有直接存取。代理商版投影少那兩欄（`?` 選填），在這裡一次補齊比在每個 view
   *   各自 `?? null` 安全——漏一處就是執行期 undefined 靜默少一塊。
   * ★ 歸屬直接填自己：代理商看得到的客戶**恆是自家客戶**（後端過濾），寫成 null 會讓
   *   任何「這是誰的客戶」判斷在代理商視角下得到錯的答案。
   */
  function fromResellerCustomer(c: ResellerCustomer, auth: ReturnType<typeof useAuthStore>): Customer {
    return {
      id: c.id,
      name: c.name,
      domain: c.domain,
      notes: c.notes,
      created_at: c.created_at,
      reseller_id: auth.resellerId,
      reseller_name: auth.resellerName,
      kind: c.kind ?? null,
      profile_completed_at: c.profile_completed_at ?? null,
    }
  }

  /**
   * ★★ 批99-B：**依 realm 分流**。代理商打 `GET /api/admin/customers` 一律 403
   * （後端 sub-router 結構性隔離），而開站精靈／主機詳情／任務頁都會呼這支——
   * 不分流的話代理商一進代管頁就整片空客戶下拉。`GET /api/admin/reseller/customers`
   * 只回自家客戶，那正好是他唯一開得了站的對象。
   */
  async function loadCustomers() {
    const auth = useAuthStore()
    if (auth.isReseller) {
      const page = await reseller.listCustomers({ limit: CUSTOMER_LOOKUP_LIMIT })
      customers.value = page.items.map((c) => fromResellerCustomer(c, auth))
      customersNextCursor.value = page.next_cursor
      customersLoaded.value = true
      return
    }
    const page = await ls.listCustomers({ limit: CUSTOMER_LOOKUP_LIMIT })
    customers.value = page.items
    customersNextCursor.value = page.next_cursor
    customersLoaded.value = true
  }
  /** 續載下一頁客戶（append）。沒有游標＝已經載完，直接回。 */
  async function loadMoreCustomers() {
    if (!customersNextCursor.value) return
    const auth = useAuthStore()
    if (auth.isReseller) {
      const page = await reseller.listCustomers({
        limit: CUSTOMER_LOOKUP_LIMIT,
        cursor: customersNextCursor.value,
      })
      customers.value = [...customers.value, ...page.items.map((c) => fromResellerCustomer(c, auth))]
      customersNextCursor.value = page.next_cursor
      return
    }
    const page = await ls.listCustomers({
      limit: CUSTOMER_LOOKUP_LIMIT,
      cursor: customersNextCursor.value,
    })
    customers.value = [...customers.value, ...page.items]
    customersNextCursor.value = page.next_cursor
  }
  async function ensureCustomers() {
    if (!customersLoaded.value) await loadCustomers()
  }
  /**
   * `opts` 是伺服器端過濾（客戶／代管主機／**產品**下拉）；不帶＝全部。
   *
   * ★ 批93-D：後端改 keyset 分頁（一頁 50 筆），這支載**第一頁**並記下續頁游標；
   *   要更多走 `loadMoreLicenses()`。過濾條件記在 `licenseFilter`，續頁時原樣帶回去
   *   ——不帶的話第二頁會變成「全部授權」而畫面看不出來。
   */
  async function loadLicenses(
    opts: {
      customerId?: string
      hostId?: string
      product?: string
      resellerId?: string
      /** 批121：狀態桶（伺服器端過濾）。 */
      bucket?: string
    } = {},
  ) {
    licenseFilter.value = opts
    const page = await ls.listLicenses(opts)
    licenses.value = page.items
    licensesNextCursor.value = page.next_cursor
  }
  /**
   * 四個桶的**全表**筆數（批121）。與清單分開拿：清單是分頁的、天生看不到全貌，
   * 而籤上的數字必須是全表的。
   *
   * ★ 吃的過濾條件要與 `loadLicenses` 相同（**不含 `bucket`**——這支回的就是每個桶各幾張）。
   * ★ 失敗不擋畫面：計數只是徽章，拿不到就維持上一次的值（清單本身照樣能用）。
   */
  async function loadLicenseCounts(
    opts: { customerId?: string; hostId?: string; product?: string; resellerId?: string } = {},
  ) {
    try {
      licenseCounts.value = await ls.licenseCounts(opts)
    } catch {
      /* 保留上一次的數字 */
    }
  }
  /** 續載下一頁（append）。沒有游標＝已經載完，直接回。 */
  async function loadMoreLicenses() {
    if (!licensesNextCursor.value) return
    const page = await ls.listLicenses({
      ...licenseFilter.value,
      cursor: licensesNextCursor.value,
    })
    licenses.value = [...licenses.value, ...page.items]
    licensesNextCursor.value = page.next_cursor
  }
  /**
   * `opts` 是伺服器端過濾（客戶／代管主機）；不帶＝全部。載**第一頁**並記下續頁游標。
   * 過濾條件記在 `deploymentFilter`，續頁時原樣帶回去——不帶的話第二頁會變成「全部部署」，
   * 而畫面完全看不出來。
   */
  async function loadDeployments(
    opts: { customerId?: string; hostId?: string; resellerId?: string; product?: string } = {},
  ) {
    deploymentFilter.value = opts
    const page = await ls.listDeployments(opts)
    deployments.value = page.items
    deploymentsNextCursor.value = page.next_cursor
  }
  async function loadMoreDeployments() {
    if (!deploymentsNextCursor.value) return
    const page = await ls.listDeployments({
      ...deploymentFilter.value,
      cursor: deploymentsNextCursor.value,
    })
    deployments.value = [...deployments.value, ...page.items]
    deploymentsNextCursor.value = page.next_cursor
  }
  async function loadRebinds() {
    const page = await ls.listRebindRequests()
    rebinds.value = page.items
    rebindsNextCursor.value = page.next_cursor
  }
  async function loadMoreRebinds() {
    if (!rebindsNextCursor.value) return
    const page = await ls.listRebindRequests({ cursor: rebindsNextCursor.value })
    rebinds.value = [...rebinds.value, ...page.items]
    rebindsNextCursor.value = page.next_cursor
  }
  /**
   * `opts` 是伺服器端過濾（來源／狀態／代理商／產品／只看被退回的）；不帶＝全部。
   * 載第一頁＋記游標。
   *
   * ★ 批121 §9：狀態頁籤與產品從前端桶下推伺服器，所以 `status`／`product`／
   *   `onlyReturned` 一律進 `requestFilter`（`loadMoreRequests` 原樣帶回去）。
   *   側邊欄徽章改讀 `requestCounts`（`loadRequestCounts`）——批93-D 留的那個
   *   「算的是已載入的那些」的債，到這裡結清。
   */
  async function loadRequests(
    opts: {
      origin?: string
      status?: string
      resellerId?: string
      /** 批141：申請人是這個多租戶平台的組織。 */
      platformId?: string
      product?: string
      /** 批155：核發方式（`platform`＝平台自動核准）。 */
      issuedVia?: string
      onlyReturned?: boolean
    } = {},
  ) {
    requestFilter.value = opts
    const page = await ls.listLicenseRequests(opts)
    requests.value = page.items
    requestsNextCursor.value = page.next_cursor
  }
  /**
   * 五個狀態桶＋`returned` 的**全表**筆數（批121 §9）。與清單分開拿：清單是分頁的、
   * 天生看不到全貌，而籤上的數字必須是全表的。
   *
   * ★ 吃的過濾條件要與 `loadRequests` 相同（**不含 `status`／`onlyReturned`**
   *   ——這支回的就是每個桶各幾筆）。
   * ★ 失敗不擋畫面：計數只是徽章，拿不到就維持上一次的值（清單本身照樣能用）。
   */
  async function loadRequestCounts(
    opts: { origin?: string; resellerId?: string; platformId?: string; product?: string; issuedVia?: string } = {},
  ) {
    try {
      requestCounts.value = await ls.licenseRequestCounts(opts)
    } catch {
      /* 保留上一次的數字 */
    }
  }
  async function loadMoreRequests() {
    if (!requestsNextCursor.value) return
    const page = await ls.listLicenseRequests({
      ...requestFilter.value,
      cursor: requestsNextCursor.value,
    })
    requests.value = [...requests.value, ...page.items]
    requestsNextCursor.value = page.next_cursor
  }
  async function loadResellers() {
    try {
      resellers.value = await ls.listResellers()
      resellersDenied.value = false
    } catch {
      // admin 讀不到（superadmin 端點）／後端還沒上這支 → 記下來讓呼叫端退化。
      resellers.value = []
      resellersDenied.value = true
    }
    resellersLoaded.value = true
  }
  async function ensureResellers() {
    if (!resellersLoaded.value) await loadResellers()
  }
  /** 代理商 id → 名稱；null／查無＝「直營」（未掛代理商就是我方自己的客戶）。 */
  function resellerName(id: string | null | undefined): string {
    if (!id) return '直營'
    return resellers.value.find((r) => r.id === id)?.name ?? id.slice(0, 8)
  }
  /**
   * 憑證設計（模組／配額／套裝）。批99-B 起**依 realm 分流**：代理商打
   * `GET /api/admin/products` 是 403（reseller rank 0），改打
   * `GET /api/admin/reseller/products`——同一支 `spec_out()`，形狀逐欄相同，
   * 差別只在**只回他範圖內的產品**（空範圖＝空清單，不是全部）。
   */
  async function loadProducts() {
    const auth = useAuthStore()
    products.value = auth.isReseller ? await reseller.listProducts() : await ls.listProducts()
    productsLoaded.value = true
  }
  async function ensureProducts() {
    if (!productsLoaded.value) await loadProducts()
  }
  async function loadMetricDefs() {
    metricDefs.value = await ls.listMetricDefs()
    metricDefsLoaded.value = true
  }
  async function ensureMetricDefs() {
    if (!metricDefsLoaded.value) await loadMetricDefs()
  }
  async function loadAgentConfigTemplates() {
    agentConfigTemplates.value = await ls.listAgentConfigTemplates()
    agentConfigTemplatesLoaded.value = true
  }
  async function ensureAgentConfigTemplates() {
    if (!agentConfigTemplatesLoaded.value) await loadAgentConfigTemplates()
  }
  /** 依 key 取憑證設計；查無（還沒載／已刪／載入失敗）＝null，呼叫端一律要能容忍。 */
  function productByKey(key: string | null | undefined): ProductSpec | null {
    if (!key) return null
    return products.value.find((p) => p.key === key) ?? null
  }
  /** 產品 key → 中文名（查無回原字串，別讓畫面出現空白欄）。 */
  function productName(key: string | null | undefined): string {
    if (!key) return '—'
    return products.value.find((p) => p.key === key)?.name ?? key
  }

  function customerName(id: string | null): string {
    return customers.value.find((c) => c.id === id)?.name ?? '—'
  }
  // ★ 批121 §9：側欄徽章的 `pendingReqCount()`／`ourDraftReqCount()` **已移除**。
  //   它們數的是「已載入的那一頁」，申請單超過 50 筆之後就會少算；改由 `AppShell.vue`
  //   直接打 `ls.licenseRequestCounts()` 拿全表數字。留著一份會少算的近似在 store 裡，
  //   遲早有人再接上去。

  return {
    customers,
    products,
    productsLoaded,
    metricDefs,
    metricDefsLoaded,
    agentConfigTemplates,
    agentConfigTemplatesLoaded,
    resellers,
    resellersLoaded,
    resellersDenied,
    licenses,
    licensesNextCursor,
    licenseCounts,
    deployments,
    deploymentsNextCursor,
    rebinds,
    rebindsNextCursor,
    requests,
    requestsNextCursor,
    requestCounts,
    customersLoaded,
    customersNextCursor,
    loadCustomers,
    loadMoreCustomers,
    ensureCustomers,
    loadLicenses,
    loadLicenseCounts,
    loadMoreLicenses,
    loadDeployments,
    loadMoreDeployments,
    loadRebinds,
    loadMoreRebinds,
    loadRequests,
    loadRequestCounts,
    loadMoreRequests,
    loadProducts,
    ensureProducts,
    loadMetricDefs,
    ensureMetricDefs,
    loadAgentConfigTemplates,
    ensureAgentConfigTemplates,
    loadResellers,
    ensureResellers,
    resellerName,
    productByKey,
    productName,
    customerName,
  }
})
